import React from "react";

import { cn } from "lib/utils";

import { SkeletonBlock, SkeletonText } from "./SkeletonPrimitives";

type TableSkeletonProps = {
  columns?: number;
  rows?: number;
  compact?: boolean;
  className?: string;
  showFilters?: boolean;
  showFooter?: boolean;
};

const TableSkeleton = ({
  columns = 4,
  rows = 6,
  compact = false,
  className,
  showFilters = false,
  showFooter = false,
}: TableSkeletonProps) => {
  const columnStyles: React.CSSProperties = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  };

  const rowPadding = compact ? "py-3" : "py-4";
  const colArray = Array.from({ length: columns });
  const rowArray = Array.from({ length: rows });

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-slate-200/70 bg-white/95",
        className,
      )}
      aria-hidden
      data-testid="table-skeleton"
    >
      <div className="border-b border-slate-200/70 bg-slate-50/70">
        <div className="grid gap-4 px-6 py-4" style={columnStyles}>
          {colArray.map((_, index) => (
            <SkeletonText key={`head-${index}`} className="h-4 w-24" />
          ))}
        </div>
      </div>

      {showFilters && (
        <div className="border-b border-slate-200/70 bg-white/90 px-6 py-3">
          <div className="flex flex-wrap gap-3">
            <SkeletonBlock className="h-9 w-32 rounded-full" />
            <SkeletonBlock className="h-9 w-24 rounded-full" />
            <SkeletonBlock className="h-9 w-28 rounded-full" />
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-200/70">
        {rowArray.map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className={cn("grid gap-4 px-6", rowPadding)}
            style={columnStyles}
          >
            {colArray.map((__, colIndex) => (
              <SkeletonBlock
                key={`cell-${rowIndex}-${colIndex}`}
                className="h-4 w-full rounded-md"
              />
            ))}
          </div>
        ))}
      </div>

      {showFooter && (
        <div className="border-t border-slate-200/70 bg-white/95 px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SkeletonText className="h-4 w-32" />
            <div className="flex items-center gap-2">
              <SkeletonBlock className="h-6 w-6 rounded-full" />
              <SkeletonBlock className="h-6 w-6 rounded-full" />
              <SkeletonBlock className="h-6 w-6 rounded-full" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableSkeleton;
